import LabelWithTooltip from "@/components/form/labels/LabelWithTooltip";
import { SwitchOptions } from "@/components/SwitchOptions";
import Ellipsis from "@/components/views/Ellipsis";
import { useWindowSize } from "@/utils/hooksUtils";
import { cssVariables, numberOrZero } from "@/utils/utils";
import { Card, Flex } from "antd";
import { useState } from "react";
import CountUp from "react-countup";
import { DATA_UNIT } from "../../../../enums";
import { DASHBOARD_MIDLLE_SCREEN_UPDATE_WIDTH } from "../../../../utils";

type PortfolioCardAmounts = { leftAmount?: number; rightAmount?: number };

type PortfolioCardLabel = { title: string; hint: string };

export type PortfolioCardData = {
  [DATA_UNIT.AMOUNT_SUM]?: PortfolioCardAmounts;
  [DATA_UNIT.CONTRACT_COUNT]: PortfolioCardAmounts;
};

export type PortfolioCardLabels = Record<
  DATA_UNIT,
  { option: string; left: PortfolioCardLabel; right: PortfolioCardLabel }
>;

type Props = {
  disabled?: boolean;
  logo: React.ReactNode;
  title: string;
  data: PortfolioCardData;
  isLoading?: boolean;
  labels: PortfolioCardLabels;
  switchWidth?: number;
};

const resolveDefaultDataUnit = (data: PortfolioCardData): DATA_UNIT =>
  data[DATA_UNIT.AMOUNT_SUM] ? DATA_UNIT.AMOUNT_SUM : DATA_UNIT.CONTRACT_COUNT;

const resolveValueFontSize = (values: number[], isLargeScreen: boolean): number => {
  const digits = Math.max(...values.map((value) => Math.abs(Math.round(value)).toString().length));

  if (digits <= 5) {
    return isLargeScreen ? 26 : 22;
  }

  if (digits <= 6) {
    return isLargeScreen ? 24 : 22;
  }

  if (digits <= 7) {
    return isLargeScreen ? 20 : 18;
  }

  return isLargeScreen ? 16 : 15;
};

const CURRENCY_FONT_SIZE = 20;

export const PortfolioCard = ({ disabled, logo, title, data, isLoading, labels, switchWidth = 200 }: Props) => {
  const [selectedDataUnit, setSelectedDataUnit] = useState<DATA_UNIT>();

  const windowSize = useWindowSize();

  const dataUnit = selectedDataUnit && data[selectedDataUnit] ? selectedDataUnit : resolveDefaultDataUnit(data);
  const leftValue = numberOrZero(data[dataUnit]?.leftAmount);
  const rightValue = numberOrZero(data[dataUnit]?.rightAmount);
  const hasCurrency = dataUnit === DATA_UNIT.AMOUNT_SUM;
  const valueFontSize = resolveValueFontSize(
    [leftValue, rightValue],
    windowSize.innerWidth >= DASHBOARD_MIDLLE_SCREEN_UPDATE_WIDTH,
  );
  const currencyFontSize = Math.min(CURRENCY_FONT_SIZE, valueFontSize);

  return (
    <Card
      loading={isLoading}
      style={{
        height: "100%",
        borderRadius: 18,
        textAlign: "center",
        borderStyle: disabled ? "dashed" : "solid",
        color: disabled ? cssVariables.colorGrey : cssVariables.colorText,
        cursor: disabled ? "not-allowed" : "default",
        backgroundColor: disabled ? cssVariables.colorHover : "white",
      }}
      styles={{ body: { height: "100%", padding: 16 } }}
    >
      <Flex vertical style={{ height: "100%" }}>
        <Flex align="center" justify="center" style={{ marginBottom: 4 }}>
          {logo}

          <span style={{ fontSize: 20, paddingLeft: 8 }}>{title}</span>
        </Flex>

        <Flex flex={1} align="center" justify="center">
          <div style={{ marginRight: 18 }}>
            <div style={{ color: cssVariables.colorGrey }}>
              <LabelWithTooltip tooltip={labels[dataUnit].left.hint} label={labels[dataUnit].left.title} />
            </div>

            <Ellipsis>
              <b
                style={{
                  fontSize: valueFontSize,
                  color: disabled ? cssVariables.colorGrey : cssVariables.colorText,
                }}
              >
                <CountUp separator={" "} duration={4} end={leftValue} />

                {hasCurrency ? <span style={{ fontSize: currencyFontSize }}>€</span> : undefined}
              </b>
            </Ellipsis>
          </div>

          <div
            style={{
              borderRight: `1px solid ${cssVariables.colorShadow}`,
              height: 50,
              transform: "rotate(20deg)",
              marginRight: 12,
            }}
          />

          <div>
            <div style={{ color: cssVariables.colorGrey, margin: "0 8px" }}>
              <LabelWithTooltip tooltip={labels[dataUnit].right.hint} label={labels[dataUnit].right.title} />
            </div>
            <Ellipsis>
              <b
                style={{
                  fontSize: valueFontSize,
                  color: disabled ? cssVariables.colorGrey : cssVariables.colorText,
                }}
              >
                <CountUp separator={" "} duration={4} end={rightValue} />

                {hasCurrency ? <span style={{ fontSize: currencyFontSize }}>€</span> : undefined}
              </b>
            </Ellipsis>
          </div>
        </Flex>

        <Flex justify="end">
          <div style={{ width: data[DATA_UNIT.AMOUNT_SUM] ? switchWidth : 100, maxWidth: "100%" }}>
            <SwitchOptions
              size="small"
              options={[
                ...(data[DATA_UNIT.AMOUNT_SUM]
                  ? [{ label: labels[DATA_UNIT.AMOUNT_SUM].option, value: DATA_UNIT.AMOUNT_SUM }]
                  : []),
                { label: labels[DATA_UNIT.CONTRACT_COUNT].option, value: DATA_UNIT.CONTRACT_COUNT },
              ]}
              onChange={setSelectedDataUnit}
              disabled={disabled}
              value={dataUnit}
            />
          </div>
        </Flex>
      </Flex>
    </Card>
  );
};
